import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { api, FirebaseContext } from 'common';
import MaterialTable from 'material-table';
import CircularLoading from '../components/CircularLoading';
import TableIcons from '../components/TableIcons';
import ShopProductEditorDialog from '../components/ShopProductEditorDialog.jsx';
import ShopCategoryEditorDialog from '../components/ShopCategoryEditorDialog.jsx';
import { hasPermission } from '../common/permissionUtils';
import { getPartnerDisplayName, normalizePartnerSlug } from '../common/partnerUtils';
import { DEFAULT_WEBSITE_CONTENT } from '../common/websiteUtils';
import {
  STORE_TEMPLATES,
  getDefaultSectionOrder,
  getHeroTheme,
  getSiteTheme,
  normalizeTemplateKey,
} from '../common/websiteTemplates';

const TemplateThumbnail = ({ templateKey }) => {
  const heroTheme = getHeroTheme(templateKey);
  const siteTheme = getSiteTheme(templateKey, {});
  const isDarkHero = heroTheme.text === '#ffffff';
  const chrome = siteTheme.border || 'rgba(15,23,42,0.10)';

  return (
    <Box
      sx={{
        height: 220,
        overflow: 'hidden',
        bgcolor: siteTheme.pageBg || '#ffffff',
        borderTop: `1px solid ${chrome}`,
      }}
    >
      <Box sx={{ px: 1.25, pt: 1.25 }}>
        {/* mini header */}
        <Box
          sx={{
            height: 16,
            borderRadius: 1,
            bgcolor: siteTheme.sectionAltBg || '#f1f5f9',
            border: `1px solid ${chrome}`,
          }}
        />

        {/* hero */}
        <Box
          sx={{
            mt: 1,
            height: 80,
            borderRadius: 2,
            background: heroTheme.background,
            position: 'relative',
            overflow: 'hidden',
            border: `1px solid ${isDarkHero ? 'rgba(255,255,255,0.16)' : chrome}`,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 12,
              bottom: 14,
              width: '55%',
              height: 8,
              borderRadius: 999,
              bgcolor: isDarkHero ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.35)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: 12,
              bottom: 6,
              width: '38%',
              height: 6,
              borderRadius: 999,
              bgcolor: isDarkHero ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.22)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: -20,
              top: -20,
              width: 90,
              height: 90,
              borderRadius: '50%',
              bgcolor: isDarkHero ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.75)',
              filter: 'blur(1px)',
            }}
          />
        </Box>

        {/* content grid */}
        <Box
          sx={{
            mt: 1,
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr 1fr',
            gap: 1,
          }}
        >
          <Box
            sx={{
              height: 60,
              borderRadius: 2,
              bgcolor: siteTheme.cardAltBg || '#f1f5f9',
              border: `1px solid ${chrome}`,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 10,
                top: 10,
                width: '70%',
                height: 6,
                borderRadius: 999,
                bgcolor: 'rgba(15,23,42,0.18)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: 10,
                top: 22,
                width: '48%',
                height: 6,
                borderRadius: 999,
                bgcolor: 'rgba(15,23,42,0.12)',
              }}
            />
          </Box>
          <Box
            sx={{
              height: 60,
              borderRadius: 2,
              bgcolor: siteTheme.cardBg || '#ffffff',
              border: `1px solid ${chrome}`,
            }}
          />
          <Box
            sx={{
              height: 60,
              borderRadius: 2,
              bgcolor: siteTheme.cardBg || '#ffffff',
              border: `1px solid ${chrome}`,
            }}
          />
        </Box>

        {/* mini footer */}
        <Box
          sx={{
            mt: 1,
            height: 14,
            borderRadius: 1,
            bgcolor: siteTheme.sectionAltBg || '#f1f5f9',
            border: `1px solid ${chrome}`,
          }}
        />
      </Box>
    </Box>
  );
};

const ImageUrlField = ({
  label,
  value,
  onChange,
  helperText,
  disabled,
  uploadPathPrefix = 'website_assets',
  previewSize = 64,
}) => {
  const firebase = useContext(FirebaseContext);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [failed, setFailed] = useState(false);

  const hasValue = Boolean(value);
  const canUpload = Boolean(firebase?.storage) && !disabled;

  useEffect(() => {
    setFailed(false);
  }, [value]);

  const handleUploadClick = () => {
    if (!canUpload) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    // Allow re-selecting the same file if needed.
    event.target.value = '';
    if (!file) return;
    if (!firebase?.storage) {
      alert('Storage is not available yet.');
      return;
    }

    setUploading(true);
    try {
      const safeName = String(file.name || 'image')
        .replace(/[^a-z0-9._-]+/gi, '_')
        .slice(0, 80);
      const path = `${uploadPathPrefix}/${Date.now()}_${safeName}`;
      const ref = firebase.storage.ref(path);
      await ref.put(file);
      const url = await ref.getDownloadURL();
      onChange(url);
    } catch (error) {
      console.error('Image upload failed:', error);
      alert('Image upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleOpen = () => {
    if (!hasValue) return;
    window.open(value, '_blank', 'noopener,noreferrer');
  };

  const handleClear = () => onChange('');

  return (
    <Stack spacing={1}>
      <TextField
        fullWidth
        label={label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        helperText={helperText}
        disabled={disabled}
      />

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudUploadRoundedIcon />}
            onClick={handleUploadClick}
            disabled={!canUpload || uploading}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewRoundedIcon />}
            onClick={handleOpen}
            disabled={!hasValue}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            Open
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<LinkOffRoundedIcon />}
            onClick={handleClear}
            disabled={!hasValue || disabled}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            Clear
          </Button>
          {uploading ? <CircularProgress size={16} sx={{ ml: 1 }} /> : null}
        </Stack>

        <Box
          sx={{
            width: previewSize,
            height: previewSize,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            bgcolor: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {hasValue && !failed ? (
            <Box
              component="img"
              alt=""
              src={value}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={() => setFailed(true)}
            />
          ) : (
            <ImageRoundedIcon sx={{ color: '#94a3b8' }} />
          )}
        </Box>
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />
    </Stack>
  );
};

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const parseDelimitedList = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v || '').trim()).filter(Boolean);
  return String(value || '')
    .split(/[,\\n]/g)
    .map((v) => v.trim())
    .filter(Boolean);
};

const stringifyDelimitedList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  return String(value || '');
};

const normalizeLangList = (langlist) => {
  if (Array.isArray(langlist)) return langlist.filter(Boolean);
  if (langlist && typeof langlist === 'object') return Object.values(langlist).filter(Boolean);
  return [];
};

const mergeDeep = (target, source) => {
  if (!source) return target;
  const output = { ...target };
  Object.keys(source).forEach((key) => {
    if (Array.isArray(source[key])) {
      output[key] = source[key];
    } else if (source[key] && typeof source[key] === 'object') {
      output[key] = mergeDeep(target[key] || {}, source[key]);
    } else if (source[key] !== undefined) {
      output[key] = source[key];
    }
  });
  return output;
};

const STORAGE_VERSION = 1;
const buildDraftStorageKey = (uid, scope) =>
  `plukk.storefront.draft.v${STORAGE_VERSION}:${uid || 'anon'}:${scope || 'global'}`;

const isPlainObject = (value) => Boolean(value && typeof value === 'object' && !Array.isArray(value));

const deepEqual = (a, b) => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
      if (!deepEqual(a[key], b[key])) return false;
    }
    return true;
  }
  return false;
};

const collectDiffRows = (baseValue, currentValue, prefix = []) => {
  if (deepEqual(baseValue, currentValue)) return [];
  if (Array.isArray(currentValue) || Array.isArray(baseValue)) {
    return [
      {
        path: prefix,
        base: baseValue,
        current: currentValue,
        kind: 'array',
      },
    ];
  }
  if (isPlainObject(currentValue) && isPlainObject(baseValue)) {
    const keys = new Set([...Object.keys(baseValue), ...Object.keys(currentValue)]);
    const rows = [];
    keys.forEach((key) => {
      if (key === 'translations') return;
      rows.push(...collectDiffRows(baseValue[key], currentValue[key], [...prefix, key]));
    });
    return rows;
  }
  return [
    {
      path: prefix,
      base: baseValue,
      current: currentValue,
      kind: 'value',
    },
  ];
};

const formatDiffValue = (value) => {
  if (value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch (e) {
    return String(value);
  }
};

const ensureWebsiteContent = (settings) => {
  const current = settings?.websiteContent;
  if (current && typeof current === 'object') return current;
  return { ...DEFAULT_WEBSITE_CONTENT };
};

export default function Storefront() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { editSettings } = api;

  const auth = useSelector((state) => state.auth);
  const settingsdata = useSelector((state) => state.settingsdata);
  const languagedata = useSelector((state) => state.languagedata);

  const settings = settingsdata.settings;
  const canUpdateSettings = hasPermission(auth.profile, settings, 'settings', 'update');

  const [scopeKey, setScopeKey] = useState('global'); // "global" | `partner:${slug}`
  const [tab, setTab] = useState(0);
  const [baseDraft, setBaseDraft] = useState(DEFAULT_WEBSITE_CONTENT);
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [shopAdminTab, setShopAdminTab] = useState(0);
  const [shopProductFilter, setShopProductFilter] = useState('all'); // all | product | service
  const [productEditor, setProductEditor] = useState({ open: false, index: -1, product: null });
  const [categoryEditor, setCategoryEditor] = useState({ open: false, index: -1, category: null });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState('classic');
  const [previewTitle, setPreviewTitle] = useState('');

  const [editLocale, setEditLocale] = useState('base');
  const [draftByLocale, setDraftByLocale] = useState({});
  const [dirtyByLocale, setDirtyByLocale] = useState({});
  const [viewMode, setViewMode] = useState('edit'); // edit | diff
  const [autosavedAt, setAutosavedAt] = useState(null);
  const [restoredAt, setRestoredAt] = useState(null);
  const [saving, setSaving] = useState(false);

  const draftStorageKey = useMemo(
    () => buildDraftStorageKey(auth?.profile?.uid, scopeKey),
    [auth?.profile?.uid, scopeKey]
  );
  const skipAutosaveRef = useRef(true);

  const languages = useMemo(() => {
    const list = normalizeLangList(languagedata?.langlist);
    return [...list].sort((a, b) => String(a.langName || '').localeCompare(String(b.langName || '')));
  }, [languagedata?.langlist]);

  const partners = useMemo(() => {
    const raw = settings?.partnerPages;
    const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];
    return [...list]
      .map((partner) => ({
        ...partner,
        _normalizedSlug: normalizePartnerSlug(partner?.slug || partner?.partnerSlug || partner?.partner || ''),
        _displayName: getPartnerDisplayName(partner, settings) || partner?.name || partner?.slug || '',
      }))
      .filter((partner) => partner._normalizedSlug)
      .sort((a, b) => String(a._displayName).localeCompare(String(b._displayName)));
  }, [settings]);

  const editorScope = useMemo(() => {
    if (scopeKey.startsWith('partner:')) {
      return { type: 'partner', slug: normalizePartnerSlug(scopeKey.slice('partner:'.length)) };
    }
    return { type: 'global', slug: null };
  }, [scopeKey]);

  const selectedPartner = useMemo(() => {
    if (editorScope.type !== 'partner') return null;
    return partners.find((partner) => partner._normalizedSlug === editorScope.slug) || null;
  }, [editorScope.type, editorScope.slug, partners]);

  const editor = useMemo(() => {
    if (!settings) {
      return {
        type: 'global',
        slug: null,
        partner: null,
        partnerEnabled: false,
        content: { ...DEFAULT_WEBSITE_CONTENT },
        overrides: {},
      };
    }

    if (editorScope.type === 'global') {
      const content = ensureWebsiteContent(settings);
      return {
        type: 'global',
        slug: null,
        partner: null,
        partnerEnabled: false,
        content,
        overrides: content,
      };
    }

    const globalContent = ensureWebsiteContent(settings);
    const overrides =
      selectedPartner?.storefrontContent && typeof selectedPartner.storefrontContent === 'object'
        ? selectedPartner.storefrontContent
        : {};
    const merged = mergeDeep(globalContent, overrides);
    return {
      type: 'partner',
      slug: editorScope.slug,
      partner: selectedPartner,
      partnerEnabled: Boolean(selectedPartner?.storefrontEnabled),
      content: merged,
      overrides,
    };
  }, [settings, editorScope.type, editorScope.slug, selectedPartner]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    skipAutosaveRef.current = true;
    setAutosavedAt(null);

    try {
      const raw = window.localStorage.getItem(draftStorageKey);
      if (!raw) {
        setEditLocale('base');
        setDraftByLocale({});
        setDirtyByLocale({});
        setViewMode('edit');
        setRestoredAt(null);
        return;
      }

      const parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== STORAGE_VERSION) {
        setEditLocale('base');
        setDraftByLocale({});
        setDirtyByLocale({});
        setViewMode('edit');
        setRestoredAt(null);
        return;
      }

      setDraftByLocale(parsed.drafts || {});
      setDirtyByLocale(parsed.dirtyByLocale || {});
      if (parsed.editLocale) setEditLocale(parsed.editLocale);
      if (parsed.viewMode) setViewMode(parsed.viewMode);
      setRestoredAt(parsed.updatedAt || Date.now());
    } catch (e) {
      setEditLocale('base');
      setDraftByLocale({});
      setDirtyByLocale({});
      setViewMode('edit');
      setRestoredAt(null);
    } finally {
      skipAutosaveRef.current = false;
    }
  }, [draftStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (skipAutosaveRef.current) return;
    try {
      const payload = {
        version: STORAGE_VERSION,
        updatedAt: Date.now(),
        editLocale,
        viewMode,
        drafts: draftByLocale,
        dirtyByLocale,
      };
      window.localStorage.setItem(draftStorageKey, JSON.stringify(payload));
      setAutosavedAt(payload.updatedAt);
    } catch (e) {
      // Ignore storage quota / private mode failures.
    }
  }, [draftStorageKey, draftByLocale, dirtyByLocale, editLocale, viewMode]);

  useEffect(() => {
    if (editorScope.type === 'partner' && !selectedPartner) {
      setScopeKey('global');
    }
  }, [editorScope.type, selectedPartner]);

  useEffect(() => {
    if (!settings) return;
    const content = editor.content;
    const nextBase = { ...DEFAULT_WEBSITE_CONTENT, ...(content || {}) };
    nextBase.enabledSections = {
      ...(DEFAULT_WEBSITE_CONTENT.enabledSections || {}),
      ...(nextBase.enabledSections || {}),
    };
    nextBase.navigation = { ...(DEFAULT_WEBSITE_CONTENT.navigation || {}), ...(nextBase.navigation || {}) };
    nextBase.appearance = nextBase.appearance && typeof nextBase.appearance === 'object' ? nextBase.appearance : {};
    nextBase.sectionOrder = Array.isArray(nextBase.sectionOrder)
      ? nextBase.sectionOrder
      : (DEFAULT_WEBSITE_CONTENT.sectionOrder || []);

    setBaseDraft(nextBase);

    const currentTemplate = normalizeTemplateKey(nextBase.templateDraft || nextBase.template);
    const allowed = new Set(STORE_TEMPLATES.map((item) => item.key));
    setSelectedTemplate(allowed.has(currentTemplate) ? currentTemplate : 'classic');
  }, [settings, editor.content]);

	  useEffect(() => {
	    if (!settings) return;
	    const content = editor.content;
	    const locale = editLocale === 'base' ? null : editLocale;
	    const baseContent = { ...DEFAULT_WEBSITE_CONTENT, ...(content || {}) };
	    const localized = locale && content?.translations?.[locale] ? content.translations[locale] : {};

    // Shallow merge is enough for the subset of fields we edit here.
    const draft = {
      ...baseContent,
      ...localized,
      branding: { ...(baseContent.branding || {}), ...(localized.branding || {}) },
      seo: { ...(baseContent.seo || {}), ...(localized.seo || {}) },
      hero: { ...(baseContent.hero || {}), ...(localized.hero || {}) },
      map: { ...(baseContent.map || {}), ...(localized.map || {}) },
    };

	    setDraftByLocale((prev) => {
	      if (dirtyByLocale?.[editLocale]) return prev;
	      return {
	        ...prev,
	        [editLocale]: draft,
	      };
	    });
	  }, [settings, editLocale, editor.content, dirtyByLocale]);

  const currentDraft = draftByLocale[editLocale] || DEFAULT_WEBSITE_CONTENT;
  const liveTemplateKey = normalizeTemplateKey(baseDraft.template);
  const savedDraftTemplateKey = normalizeTemplateKey(baseDraft.templateDraft || baseDraft.template);
  const hasUnsavedDraft = Boolean(dirtyByLocale?.[editLocale]);

  const diffRows = useMemo(() => {
    if (editLocale === 'base') return [];
    const base = draftByLocale.base || DEFAULT_WEBSITE_CONTENT;
    const subsetBase = {
      branding: base.branding,
      seo: base.seo,
      hero: base.hero,
      map: base.map,
    };
    const subsetCurrent = {
      branding: currentDraft.branding,
      seo: currentDraft.seo,
      hero: currentDraft.hero,
      map: currentDraft.map,
    };
    return collectDiffRows(subsetBase, subsetCurrent).map((row, idx) => ({
      id: idx,
      path: row.path.join('.'),
      base: formatDiffValue(row.base),
      current: formatDiffValue(row.current),
    }));
  }, [editLocale, draftByLocale.base, currentDraft]);

  const resolveTemplateLabel = (key) => {
    const tpl = STORE_TEMPLATES.find((item) => item.key === key);
    if (!tpl) return key;
    const translated = t(tpl.labelKey);
    return translated !== tpl.labelKey ? translated : tpl.fallbackLabel;
  };

  const orderedSectionKeys = useMemo(() => {
    const available = ['hero', 'map', 'howItWorks', 'valueProps', 'driverBusiness', 'download'];
    const fromDraft = Array.isArray(baseDraft.sectionOrder) ? baseDraft.sectionOrder : [];
    const next = fromDraft.filter((key) => available.includes(key));
    available.forEach((key) => {
      if (!next.includes(key)) next.push(key);
    });
    return next;
  }, [baseDraft.sectionOrder]);

  const sectionLabelByKey = useMemo(
    () => ({
      hero: t('hero_section') || 'Hero',
      map: t('map_section') || 'Map',
      howItWorks: t('how_it_works') || 'How it works',
      valueProps: t('value_props') || 'Value props',
      driverBusiness: t('driver_business') || 'Driver & Business',
      download: t('download') || 'Download',
    }),
    [t]
  );

  const effectiveTheme = useMemo(
    () => getSiteTheme(selectedTemplate, baseDraft.appearance || {}),
    [selectedTemplate, baseDraft.appearance]
  );

  useEffect(() => {
    if (tab !== 5) setShopAdminTab(0);
  }, [tab]);

  useEffect(() => {
    if (editLocale === 'base' && viewMode === 'diff') {
      setViewMode('edit');
    }
  }, [editLocale, viewMode]);

  const openPreview = (templateKey, title) => {
    setPreviewTemplate(templateKey);
    setPreviewTitle(title);
    setPreviewOpen(true);
  };

  const updateDraft = (section, field, value) => {
    setDraftByLocale((prev) => {
      const existing = prev[editLocale] || DEFAULT_WEBSITE_CONTENT;
      const next = {
        ...existing,
        [section]: {
          ...(existing[section] || {}),
          [field]: value,
        },
      };
      return { ...prev, [editLocale]: next };
    });
    setDirtyByLocale((prev) => ({ ...prev, [editLocale]: true }));
  };

  const ensureCanEdit = () => {
    if (!canUpdateSettings) {
      alert(t('no_access') || 'No access');
      return false;
    }
    if (settings?.AllowCriticalEditsAdmin === false) {
      alert(t('demo_mode') || 'Demo mode');
      return false;
    }
    return true;
  };

  const updatePartnerPages = (updater) => {
    const partnerPages = settings?.partnerPages;
    const targetSlug = editor.slug ? normalizePartnerSlug(editor.slug) : '';
    if (!partnerPages || !targetSlug) return null;

    if (Array.isArray(partnerPages)) {
      const next = partnerPages.map((partner) => {
        const slug = normalizePartnerSlug(partner?.slug || partner?.partnerSlug || partner?.partner || '');
        if (slug !== targetSlug) return partner;
        return updater(partner || {});
      });
      return next;
    }

    if (typeof partnerPages === 'object') {
      const next = { ...partnerPages };
      let updated = false;
      Object.entries(partnerPages).forEach(([key, value]) => {
        const slug = normalizePartnerSlug(value?.slug || value?.partnerSlug || value?.partner || '');
        if (slug !== targetSlug) return;
        next[key] = updater(value || {});
        updated = true;
      });
      if (!updated) {
        next[targetSlug] = updater({});
      }
      return next;
    }

    return null;
  };

  const saveWebsiteContentPatch = (patch) => {
    if (!settings) return false;
    if (!ensureCanEdit()) return false;

    setSaving(true);
    if (editor.type === 'partner') {
      const nextPartnerPages = updatePartnerPages((partner) => {
        const existing =
          partner?.storefrontContent && typeof partner.storefrontContent === 'object' ? partner.storefrontContent : {};
        return {
          ...partner,
          storefrontContent: {
            ...existing,
            ...patch,
          },
        };
      });
      if (!nextPartnerPages) {
        setSaving(false);
        alert('Partner not found.');
        return false;
      }
      dispatch(
        editSettings({
          ...settings,
          partnerPages: nextPartnerPages,
        })
      );
    } else {
      const content = ensureWebsiteContent(settings);
      dispatch(
        editSettings({
          ...settings,
          websiteContent: {
            ...content,
            ...patch,
          },
        })
      );
    }
    setSaving(false);
    alert(t('updated') || 'Updated');
    return true;
  };

  const saveTemplateDraft = () => saveWebsiteContentPatch({ templateDraft: selectedTemplate });

  const publishTemplate = () =>
    saveWebsiteContentPatch({
      template: selectedTemplate,
      templateDraft: selectedTemplate,
    });

  const setAppearanceValue = (key, value) => {
    setBaseDraft((prev) => {
      const nextAppearance = { ...(prev.appearance || {}) };
      if (value === '' || value === null || value === undefined) {
        delete nextAppearance[key];
      } else {
        nextAppearance[key] = value;
      }
      return { ...prev, appearance: nextAppearance };
    });
  };

  const setFooterAppearanceValue = (key, value) => {
    setBaseDraft((prev) => {
      const nextAppearance = { ...(prev.appearance || {}) };
      const nextFooter = { ...((prev.appearance || {}).footer || {}) };
      if (value === '' || value === null || value === undefined) {
        delete nextFooter[key];
      } else {
        nextFooter[key] = value;
      }
      if (Object.keys(nextFooter).length) nextAppearance.footer = nextFooter;
      else delete nextAppearance.footer;
      return { ...prev, appearance: nextAppearance };
    });
  };

  const setNavigationGroup = (key, nextList) => {
    setBaseDraft((prev) => ({
      ...prev,
      navigation: {
        ...(prev.navigation || {}),
        [key]: nextList,
      },
    }));
  };

  const setShopValue = (key, value) => {
    setBaseDraft((prev) => ({
      ...prev,
      shop: {
        ...(prev.shop || {}),
        [key]: value,
      },
    }));
  };

  const closeProductEditor = () => setProductEditor({ open: false, index: -1, product: null });

  const openProductEditor = (product, index) => {
    setProductEditor({ open: true, index: typeof index === 'number' ? index : -1, product: product || null });
  };

  const handleSaveShopProduct = (nextProduct) => {
    const current = Array.isArray(baseDraft.shop?.products) ? [...baseDraft.shop.products] : [];
    const editingIndex = productEditor.index;
    const baseId = nextProduct?.id || slugify(nextProduct?.title) || `item-${Date.now()}`;

    const generateUniqueId = (id, existingProducts, excludeIndex) => {
      const isIdTaken = (testId) => {
        for (let idx = 0; idx < existingProducts.length; idx++) {
          if (idx !== excludeIndex && String(existingProducts[idx]?.id || '') === testId) {
            return true;
          }
        }
        return false;
      };

      let uniqueId = id;
      let counter = 2;
      while (isIdTaken(uniqueId)) {
        uniqueId = `${id}-${counter++}`;
      }
      return uniqueId;
    };

    const uniqueId = generateUniqueId(baseId, current, editingIndex);

    const normalized = {
      ...(nextProduct || {}),
      id: uniqueId,
      type: nextProduct?.type === 'service' ? 'service' : 'product',
      price: Number(nextProduct?.price || 0),
      compareAtPrice:
        nextProduct?.compareAtPrice === '' || nextProduct?.compareAtPrice === null || nextProduct?.compareAtPrice === undefined
          ? undefined
          : Number(nextProduct?.compareAtPrice || 0),
      published: nextProduct?.published !== false,
    };

    if (typeof editingIndex === 'number' && editingIndex >= 0 && editingIndex < current.length) {
      current[editingIndex] = normalized;
    } else {
      current.push(normalized);
    }

    setShopValue('products', current);
    closeProductEditor();
  };

  const closeCategoryEditor = () => setCategoryEditor({ open: false, index: -1, category: null });

  const openCategoryEditor = (category, index) => {
    setCategoryEditor({ open: true, index: typeof index === 'number' ? index : -1, category: category || null });
  };

  const handleSaveShopCategory = (nextCategory) => {
    const current = Array.isArray(baseDraft.shop?.categories) ? [...baseDraft.shop.categories] : [];
    const editingIndex = categoryEditor.index;
    const baseId = nextCategory?.id || slugify(nextCategory?.title) || `category-${Date.now()}`;

    const generateUniqueId = (id, existingCategories, excludeIndex) => {
      const isIdTaken = (testId) => {
        for (let idx = 0; idx < existingCategories.length; idx++) {
          if (idx !== excludeIndex && String(existingCategories[idx]?.id || '') === testId) {
            return true;
          }
        }
        return false;
      };

      let uniqueId = id;
      let counter = 2;
      while (isIdTaken(uniqueId)) {
        uniqueId = `${id}-${counter++}`;
      }
      return uniqueId;
    };

    const uniqueId = generateUniqueId(baseId, current, editingIndex);
    const normalized = {
      ...(nextCategory || {}),
      id: uniqueId,
      published: nextCategory?.published !== false,
      featured: Boolean(nextCategory?.featured),
      parentId: nextCategory?.parentId || '',
    };

    if (typeof editingIndex === 'number' && editingIndex >= 0 && editingIndex < current.length) {
      current[editingIndex] = normalized;
    } else {
      current.push(normalized);
    }

    setShopValue('categories', current);
    closeCategoryEditor();
  };

  const setSectionEnabled = (key, enabled) => {
    setBaseDraft((prev) => ({
      ...prev,
      enabledSections: {
        ...(prev.enabledSections || {}),
        [key]: enabled,
      },
    }));
  };

  const moveSection = (key, delta) => {
    setBaseDraft((prev) => {
      const order = Array.isArray(prev.sectionOrder) ? [...prev.sectionOrder] : [];
      const idx = order.indexOf(key);
      if (idx < 0) return prev;
      const nextIdx = idx + delta;
      if (nextIdx < 0 || nextIdx >= order.length) return prev;
      const nextOrder = [...order];
      [nextOrder[idx], nextOrder[nextIdx]] = [nextOrder[nextIdx], nextOrder[idx]];
      return { ...prev, sectionOrder: nextOrder };
    });
  };

  const cleanAppearance = (appearance) => {
    if (!appearance || typeof appearance !== 'object') return {};
    const footer = appearance.footer && typeof appearance.footer === 'object' ? appearance.footer : null;
    const next = {};
    Object.entries(appearance).forEach(([key, value]) => {
      if (key === 'footer') return;
      if (value === '' || value === null || value === undefined) return;
      next[key] = value;
    });
    if (footer) {
      const nextFooter = {};
      Object.entries(footer).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined) return;
        nextFooter[key] = value;
      });
      if (Object.keys(nextFooter).length) next.footer = nextFooter;
    }
    return next;
  };

  const saveHomepageCopy = () => {
    if (!settings) return;
    if (!ensureCanEdit()) return;

    setSaving(true);
    const content = editor.type === 'partner' ? editor.overrides || {} : ensureWebsiteContent(settings);
    const existingTranslations =
      content.translations && typeof content.translations === 'object' ? content.translations : {};

    const subset = {
      branding: { ...currentDraft.branding },
      seo: { ...currentDraft.seo },
      hero: { ...currentDraft.hero },
      map: { ...currentDraft.map },
    };

    let nextContent;
    if (editLocale === 'base') {
      nextContent = {
        ...content,
        ...subset,
      };
    } else {
      nextContent = {
        ...content,
        translations: {
          ...existingTranslations,
          [editLocale]: {
            ...(existingTranslations[editLocale] || {}),
            ...subset,
          },
        },
      };
    }

    if (editor.type === 'partner') {
      const nextPartnerPages = updatePartnerPages((partner) => ({
        ...partner,
        storefrontContent: nextContent,
      }));
      if (!nextPartnerPages) {
        setSaving(false);
        alert('Partner not found.');
        return;
      }
      dispatch(
        editSettings({
          ...settings,
          partnerPages: nextPartnerPages,
        })
      );
    } else {
      dispatch(
        editSettings({
          ...settings,
          websiteContent: nextContent,
        })
      );
    }
    setSaving(false);
    alert(t('updated') || 'Updated');
    setDirtyByLocale((prev) => ({ ...prev, [editLocale]: false }));
  };

  const setPartnerStorefrontEnabled = (enabled) => {
    if (editor.type !== 'partner') return;
    if (!ensureCanEdit()) return;
    setSaving(true);
    const nextPartnerPages = updatePartnerPages((partner) => ({
      ...partner,
      storefrontEnabled: Boolean(enabled),
    }));
    if (!nextPartnerPages) {
      setSaving(false);
      alert('Partner not found.');
      return;
    }
    dispatch(
      editSettings({
        ...settings,
        partnerPages: nextPartnerPages,
      })
    );
    setSaving(false);
  };

  const detachPartnerStorefront = () => {
    if (editor.type !== 'partner') return;
    if (!ensureCanEdit()) return;
    const globalContent = ensureWebsiteContent(settings);
    const full = mergeDeep(globalContent, editor.overrides || {});
    setSaving(true);
    const nextPartnerPages = updatePartnerPages((partner) => ({
      ...partner,
      storefrontEnabled: true,
      storefrontContent: full,
    }));
    if (!nextPartnerPages) {
      setSaving(false);
      alert('Partner not found.');
      return;
    }
    dispatch(
      editSettings({
        ...settings,
        partnerPages: nextPartnerPages,
      })
    );
    setSaving(false);
    alert(t('updated') || 'Updated');
  };

  const resetPartnerStorefront = () => {
    if (editor.type !== 'partner') return;
    if (!ensureCanEdit()) return;
    const confirmed = window.confirm('Reset this partner storefront to inherit from global?');
    if (!confirmed) return;
    setSaving(true);
    const nextPartnerPages = updatePartnerPages((partner) => ({
      ...partner,
      storefrontContent: {},
    }));
    if (!nextPartnerPages) {
      setSaving(false);
      alert('Partner not found.');
      return;
    }
    dispatch(
      editSettings({
        ...settings,
        partnerPages: nextPartnerPages,
      })
    );
    setSaving(false);
    alert(t('updated') || 'Updated');
  };

  const LinkGroupEditor = ({
    groupKey,
    title,
    description,
    allowAuth = false,
    defaultItems = [],
  }) => {
    const raw = baseDraft.navigation?.[groupKey];
    const list = Array.isArray(raw) ? raw : defaultItems;
    const safeList = list.length ? list : defaultItems;

    const setItem = (idx, patch) => {
      const next = safeList.map((item, i) => (i === idx ? { ...item, ...patch } : item));
      setNavigationGroup(groupKey, next);
    };

    const removeItem = (idx) => {
      const next = safeList.filter((_, i) => i !== idx);
      setNavigationGroup(groupKey, next);
    };

    const addItem = () => {
      const base = { label: '', path: '/', visible: true };
      const next = [...safeList, allowAuth ? { ...base, requiresAuth: false } : base];
      setNavigationGroup(groupKey, next);
    };

    return (
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography sx={{ fontWeight: 900, color: '#111827' }}>{title}</Typography>
            {description ? (
              <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.25 }}>
                {description}
              </Typography>
            ) : null}
          </Box>
          <Button
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            onClick={addItem}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
          >
            {t('add') || 'Add'}
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={1.5}>
          {safeList.map((item, idx) => (
            <Paper
              key={`${groupKey}-${idx}`}
              elevation={0}
              sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e5e7eb', bgcolor: '#fff' }}
            >
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('label') || 'Label'}
                    value={item.label || ''}
                    onChange={(e) => setItem(idx, { label: e.target.value })}
                    helperText={t('tip_translation_keys') || 'Tip: you can use translation keys (e.g. shop)'}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('path') || 'Path'}
                    value={item.path || ''}
                    onChange={(e) => setItem(idx, { path: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={item.visible !== false}
                          onChange={(_, checked) => setItem(idx, { visible: checked })}
                        />
                      }
                      label={t('visible') || 'Visible'}
                    />
                    {allowAuth ? (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(item.requiresAuth)}
                            onChange={(_, checked) => setItem(idx, { requiresAuth: checked })}
                          />
                        }
                        label={t('auth') || 'Auth'}
                      />
                    ) : null}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={1}>
                  <IconButton color="error" onClick={() => removeItem(idx)}>
                    <DeleteRoundedIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>
      </Paper>
    );
  };

  if (!settings) {
    return <CircularLoading />;
  }

	  return (
	    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
	      {/* Title bar */}
	      <Stack
	        direction="row"
	        alignItems="flex-end"
	        justifyContent="space-between"
	        sx={{ mt: 2, mb: 2, pb: 1.5, borderBottom: '1px solid #e5e7eb' }}
	      >
	        <Box>
	          <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827' }}>
	            {editor.type === 'partner'
	              ? `${t('storefront') || 'Storefront'} - ${selectedPartner?._displayName || editor.slug}`
	              : (t('storefront') || 'Storefront')}
	          </Typography>
	          <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
	            {editor.type === 'partner'
	              ? (t('storefront_partner_subtitle') ||
	                  'Edit a white-label storefront override for this partner. Unset fields inherit from global.')
	              : (t('storefront_subtitle') ||
	                  'Customize the main homepage experience for the primary domain.')}
	          </Typography>
	        </Box>
	        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
	          <TextField
	            select
	            size="small"
	            label={t('scope') || 'Scope'}
	            value={scopeKey}
	            onChange={(e) => setScopeKey(e.target.value)}
	            sx={{ minWidth: 240 }}
	          >
	            <MenuItem value="global">{t('global_storefront') || 'Global (main domain)'}</MenuItem>
	            {partners.map((partner) => (
	              <MenuItem key={partner._normalizedSlug} value={`partner:${partner._normalizedSlug}`}>
	                {partner._displayName || partner._normalizedSlug}
	              </MenuItem>
	            ))}
	          </TextField>
	          {editor.type === 'partner' ? (
	            <FormControlLabel
	              sx={{ ml: 1 }}
	              control={
	                <Switch
	                  checked={editor.partnerEnabled}
	                  onChange={(_, checked) => setPartnerStorefrontEnabled(checked)}
	                />
	              }
	              label={t('enable_partner_storefront') || 'Enable'}
	            />
	          ) : null}
	          {editor.type === 'partner' ? (
	            <Button variant="outlined" onClick={detachPartnerStorefront} disabled={saving || !canUpdateSettings}>
	              {t('detach') || 'Detach'}
	            </Button>
	          ) : null}
	          {editor.type === 'partner' ? (
	            <Button variant="outlined" color="error" onClick={resetPartnerStorefront} disabled={saving || !canUpdateSettings}>
	              {t('reset') || 'Reset'}
	            </Button>
	          ) : null}
	          <Button
	            variant="outlined"
	            startIcon={<PublicRoundedIcon />}
	            onClick={() =>
	              window.open(
	                editor.type === 'partner' ? `/${editor.slug}?storefront=1` : '/',
	                '_blank',
	                'noopener,noreferrer'
	              )
	            }
	          >
	            {t('preview_site') || 'Preview'}
	          </Button>
	        </Stack>
	      </Stack>

      <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)}>
            <Tab label={t('select_homepage') || 'Select Homepage'} />
            <Tab label={t('homepage_content') || 'Homepage Content'} />
            <Tab label={t('sections') || 'Sections'} />
            <Tab label={t('navigation') || 'Navigation'} />
            <Tab label={t('appearance') || 'Appearance'} />
            <Tab label={t('shop') || 'Shop'} />
          </Tabs>
        </Box>

        {/* Tab 0: Select Homepage */}
        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 700 }}>
                        {t('current_live_template') || 'Current live template'}
                      </Typography>
                      <Typography sx={{ fontWeight: 900, color: '#111827', mt: 0.5 }}>
                        {resolveTemplateLabel(liveTemplateKey)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={t('live') || 'Live'}
                      sx={{ bgcolor: '#16a34a', color: '#fff', fontWeight: 900 }}
                    />
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 700 }}>
                        {t('draft_selection') || 'Draft selection'}
                      </Typography>
                      <Typography sx={{ fontWeight: 900, color: '#111827', mt: 0.5 }}>
                        {resolveTemplateLabel(selectedTemplate)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.25 }}>
                        {(t('saved_draft') || 'Saved draft') + ': ' + resolveTemplateLabel(savedDraftTemplateKey)}
                      </Typography>
                    </Box>
                    {selectedTemplate !== savedDraftTemplateKey ? (
                      <Chip size="small" label={t('not_saved') || 'Not saved'} variant="outlined" />
                    ) : (
                      <Chip size="small" label={t('saved') || 'Saved'} sx={{ fontWeight: 900 }} />
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              {STORE_TEMPLATES.map((tpl) => {
                const selected = selectedTemplate === tpl.key;
                const translated = t(tpl.labelKey);
                const label = translated !== tpl.labelKey ? translated : tpl.fallbackLabel;

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={tpl.key}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        borderColor: selected ? '#111827' : '#e5e7eb',
                        boxShadow: selected ? '0 12px 30px rgba(15,23,42,0.12)' : 'none',
                        overflow: 'hidden',
                      }}
                    >
                      <CardActionArea onClick={() => setSelectedTemplate(tpl.key)}>
                        <TemplateThumbnail templateKey={tpl.key} />
                        <CardContent sx={{ py: 1.25 }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={1}
                          >
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: 14 }}>
                                {label}
                              </Typography>
                              <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }}>
                                {tpl.key === liveTemplateKey ? (
                                  <Chip
                                    size="small"
                                    label={t('live') || 'Live'}
                                    sx={{ bgcolor: '#16a34a', color: '#fff', fontWeight: 800 }}
                                  />
                                ) : null}
                                {tpl.key === savedDraftTemplateKey ? (
                                  <Chip
                                    size="small"
                                    label={t('draft') || 'Draft'}
                                    variant="outlined"
                                    sx={{ fontWeight: 800 }}
                                  />
                                ) : null}
                              </Stack>
                            </Box>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openPreview(tpl.key, label);
                              }}
                              startIcon={<VisibilityRoundedIcon />}
                              sx={{ borderRadius: 1.5, textTransform: 'none' }}
                            >
                              {t('view') || 'View'}
                            </Button>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: '#f9fafb',
                border: '1px solid #e5e7eb',
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: '#eef2ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <TuneRoundedIcon sx={{ color: '#4f46e5' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: '#111827' }}>
                        {t('configure_page_layout') || 'Configure your page layout'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {t('homepage_layout_help') ||
                          'Each homepage has a different layout. Choose one to apply on the main domain.'}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack
                    direction="row"
                    justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                    spacing={1}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<SaveRoundedIcon />}
                      onClick={saveTemplateDraft}
                      disabled={saving || !canUpdateSettings}
                      sx={{
                        minWidth: 160,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 800,
                      }}
                    >
                      {t('save_draft') || 'Save draft'}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SaveRoundedIcon />}
                      onClick={publishTemplate}
                      disabled={saving || !canUpdateSettings}
                      sx={{
                        minWidth: 180,
                        borderRadius: 2,
                        bgcolor: '#111827',
                        '&:hover': { bgcolor: '#0b1224' },
                        textTransform: 'none',
                        fontWeight: 800,
                      }}
                    >
                      {t('publish_live') || 'Publish live'}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Tab 1: Homepage Content */}
        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: '#ecfeff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ImageRoundedIcon sx={{ color: '#0891b2' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: '#111827' }}>
                        {t('homepage_content') || 'Homepage Content'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {t('homepage_content_help') || 'Edit the main homepage copy (multi-language).'}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<SaveRoundedIcon />}
                  onClick={saveHomepageCopy}
                  disabled={saving || !canUpdateSettings}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                >
                  {t('save') || 'Save'}
                </Button>
              </Stack>

              <Box sx={{ mt: 2 }}>
                <Tabs
                  value={editLocale}
                  onChange={(_, value) => setEditLocale(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab value="base" label={t('default') || 'Default'} />
                  {languages.map((lang) => {
                    const code = lang?.langLocale || lang?.id;
                    if (!code) return null;
                    return <Tab key={code} value={code} label={lang?.langName || code.toUpperCase()} />;
                  })}
                </Tabs>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                >
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={viewMode}
                    onChange={(_, value) => setViewMode(value || 'edit')}
                    sx={{ borderRadius: 999 }}
                  >
                    <ToggleButton value="edit" sx={{ textTransform: 'none', fontWeight: 800 }}>
                      {t('edit') || 'Edit'}
                    </ToggleButton>
                    <ToggleButton
                      value="diff"
                      disabled={editLocale === 'base'}
                      sx={{ textTransform: 'none', fontWeight: 800 }}
                    >
                      {t('diff') || 'Diff'}
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {hasUnsavedDraft ? (
                      <Chip size="small" label={t('draft') || 'Draft'} variant="outlined" sx={{ fontWeight: 800 }} />
                    ) : (
                      <Chip size="small" label={t('saved') || 'Saved'} sx={{ fontWeight: 800 }} />
                    )}
                    {autosavedAt ? (
                      <Chip
                        size="small"
                        label={`Autosaved ${new Date(autosavedAt).toLocaleTimeString()}`}
                        sx={{ fontWeight: 700, color: '#0f172a', bgcolor: '#e2e8f0' }}
                      />
                    ) : null}
                    {restoredAt ? (
                      <Chip
                        size="small"
                        label={`Restored ${new Date(restoredAt).toLocaleTimeString()}`}
                        sx={{ fontWeight: 700, color: '#0f172a', bgcolor: '#fef3c7' }}
                      />
                    ) : null}
                  </Stack>
                </Stack>
              </Box>

              {viewMode === 'diff' && editLocale !== 'base' ? (
                <Box sx={{ mt: 3 }}>
                  <MaterialTable
                    icons={TableIcons}
                    title={t('language_diff') || 'Language diff'}
                    columns={[
                      { title: t('field') || 'Field', field: 'path' },
                      { title: t('base') || 'Base', field: 'base' },
                      { title: t('current') || 'Current', field: 'current' },
                    ]}
                    data={diffRows}
                    options={{
                      paging: false,
                      search: true,
                      headerStyle: { fontWeight: 800 },
                      rowStyle: { fontSize: 13 },
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ mt: 3 }}>
                <Typography sx={{ fontWeight: 800, color: '#111827', mb: 1 }}>
                  {t('branding_seo') || 'Branding & SEO'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label={t('brand_name') || 'Brand name'}
                      value={currentDraft.branding?.brandName || ''}
                      onChange={(e) => updateDraft('branding', 'brandName', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ImageUrlField
                      label={t('brand_logo') || 'Logo URL'}
                      value={currentDraft.branding?.logoUrl || ''}
                      onChange={(next) => updateDraft('branding', 'logoUrl', next)}
                      uploadPathPrefix="website_assets/storefront/branding"
                      disabled={!canUpdateSettings}
                      previewSize={64}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <ImageUrlField
                      label={t('favicon_url') || 'Favicon URL'}
                      value={currentDraft.branding?.faviconUrl || ''}
                      onChange={(next) => updateDraft('branding', 'faviconUrl', next)}
                      uploadPathPrefix="website_assets/storefront/favicon"
                      disabled={!canUpdateSettings}
                      previewSize={40}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('seo_title') || 'SEO title'}
                      value={currentDraft.seo?.title || ''}
                      onChange={(e) => updateDraft('seo', 'title', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ImageUrlField
                      label={t('og_image') || 'OpenGraph image'}
                      value={currentDraft.seo?.ogImage || ''}
                      onChange={(next) => updateDraft('seo', 'ogImage', next)}
                      uploadPathPrefix="website_assets/storefront/seo"
                      disabled={!canUpdateSettings}
                      previewSize={72}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label={t('seo_description') || 'SEO description'}
                      value={currentDraft.seo?.description || ''}
                      onChange={(e) => updateDraft('seo', 'description', e.target.value)}
                    />
                  </Grid>
                </Grid>

                <Typography sx={{ fontWeight: 800, color: '#111827', mt: 4, mb: 1 }}>
                  {t('hero_section') || 'Hero section'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('hero_title_label') || 'Hero title'}
                      value={currentDraft.hero?.title || ''}
                      onChange={(e) => updateDraft('hero', 'title', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ImageUrlField
                      label={t('hero_image_label') || 'Hero image URL'}
                      value={currentDraft.hero?.image || ''}
                      onChange={(next) => updateDraft('hero', 'image', next)}
                      uploadPathPrefix="website_assets/storefront/hero"
                      disabled={!canUpdateSettings}
                      previewSize={72}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label={t('hero_subtitle_label') || 'Hero subtitle'}
                      value={currentDraft.hero?.subtitle || ''}
                      onChange={(e) => updateDraft('hero', 'subtitle', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('primary_cta_label') || 'Primary CTA'}
                      value={currentDraft.hero?.primaryCtaLabel || ''}
                      onChange={(e) => updateDraft('hero', 'primaryCtaLabel', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('secondary_cta_label') || 'Secondary CTA'}
                      value={currentDraft.hero?.secondaryCtaLabel || ''}
                      onChange={(e) => updateDraft('hero', 'secondaryCtaLabel', e.target.value)}
                    />
                  </Grid>
                </Grid>

                <Typography sx={{ fontWeight: 800, color: '#111827', mt: 4, mb: 1 }}>
                  {t('map_section') || 'Map section'}
                </Typography>
	                <Grid container spacing={2}>
	                  <Grid item xs={12} md={6}>
	                    <TextField
	                      fullWidth
	                      label={t('map_title_label') || 'Map section title'}
	                      value={currentDraft.map?.title || ''}
	                      onChange={(e) => updateDraft('map', 'title', e.target.value)}
	                    />
	                  </Grid>
	                </Grid>
	              </Box>
	            )}
	            </Paper>
	          </Box>
	        )}

        {/* Tab 2: Sections */}
        {tab === 2 && (
          <Box sx={{ p: 2 }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, color: '#111827' }}>
                    {t('sections') || 'Sections'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                    {t('sections_help') ||
                      'Enable, disable, and reorder sections on the main landing page.'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<RestartAltRoundedIcon />}
                    onClick={() =>
                      setBaseDraft((prev) => ({
                        ...prev,
                        sectionOrder: getDefaultSectionOrder(selectedTemplate),
                      }))
                    }
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                  >
                    {t('reset_order') || 'Reset order'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={() =>
                      saveWebsiteContentPatch({
                        enabledSections: baseDraft.enabledSections,
                        sectionOrder: baseDraft.sectionOrder,
                      })
                    }
                    disabled={saving || !canUpdateSettings}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                  >
                    {t('save') || 'Save'}
                  </Button>
                </Stack>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1.25}>
                {orderedSectionKeys.map((key, idx) => {
                  const enabled = baseDraft.enabledSections?.[key] !== false;
                  return (
                    <Box
                      key={key}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: '1px solid #e5e7eb',
                        bgcolor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Stack spacing={0} sx={{ mr: 0.5 }}>
                          <IconButton
                            size="small"
                            disabled={idx === 0}
                            onClick={() => moveSection(key, -1)}
                          >
                            <KeyboardArrowUpRoundedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={idx === orderedSectionKeys.length - 1}
                            onClick={() => moveSection(key, 1)}
                          >
                            <KeyboardArrowDownRoundedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <Box>
                          <Typography sx={{ fontWeight: 900, color: '#111827' }}>
                            {sectionLabelByKey[key] || key}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {enabled ? (t('enabled') || 'Enabled') : (t('disabled') || 'Disabled')}
                          </Typography>
                        </Box>
                      </Stack>

                      <Switch
                        checked={enabled}
                        onChange={(_, checked) => setSectionEnabled(key, checked)}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          </Box>
        )}

        {/* Tab 3: Navigation */}
        {tab === 3 && (
          <Box sx={{ p: 2 }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, color: '#111827' }}>
                    {t('navigation') || 'Navigation'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                    {t('navigation_help') ||
                      'Edit header and footer links for the main domain website.'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<SaveRoundedIcon />}
                  onClick={() => saveWebsiteContentPatch({ navigation: baseDraft.navigation })}
                  disabled={saving || !canUpdateSettings}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                >
                  {t('save') || 'Save'}
                </Button>
              </Stack>
            </Paper>

            <Stack spacing={2} sx={{ mt: 2 }}>
              <LinkGroupEditor
                groupKey="rideLinks"
                title={t('ride_menu_links') || 'Ride menu links'}
                description={t('ride_menu_help') || 'Shown in the header under Ride.'}
                allowAuth
                defaultItems={[
                  { label: 'home', path: '/', visible: true },
                  { label: 'book_a_ride', path: '/addbookings', visible: true, requiresAuth: true },
                  { label: 'shop', path: '/shop', visible: true },
                ]}
              />

              <LinkGroupEditor
                groupKey="driveLinks"
                title={t('drive_menu_links') || 'Drive menu links'}
                description={t('drive_menu_help') || 'Shown in the header under Drive.'}
                allowAuth
                defaultItems={[
                  { label: 'become_a_driver', path: '/login', visible: true },
                  { label: 'become_partner', path: '/contact-us', visible: true },
                ]}
              />

              <LinkGroupEditor
                groupKey="aboutLinks"
                title={t('about_menu_links') || 'About menu links'}
                description={t('about_menu_help') || 'Shown in the header under About.'}
                defaultItems={[
                  { label: 'about_us', path: '/about-us', visible: true },
                  { label: 'contact_us', path: '/contact-us', visible: true },
                  { label: 'privacy_policy', path: '/privacy-policy', visible: true },
                ]}
              />

              <LinkGroupEditor
                groupKey="footerCompany"
                title={t('footer_company_links') || 'Footer company links'}
                defaultItems={[
                  { label: 'about_us', path: '/about-us', visible: true },
                  { label: 'contact_us', path: '/contact-us', visible: true },
                  { label: 'privacy_policy', path: '/privacy-policy', visible: true },
                ]}
              />

              <LinkGroupEditor
                groupKey="footerProducts"
                title={t('footer_product_links') || 'Footer product links'}
                defaultItems={[
                  { label: 'ride', path: '/', visible: true },
                  { label: 'drive', path: '/login', visible: true },
                  { label: 'shop', path: '/shop', visible: true },
                ]}
              />

              <LinkGroupEditor
                groupKey="footerLegal"
                title={t('footer_legal_links') || 'Footer legal links'}
                defaultItems={[
                  { label: 'privacy_policy', path: '/privacy-policy', visible: true },
                  { label: 'terms', path: '/privacy-policy', visible: true },
                ]}
              />
            </Stack>
          </Box>
        )}

        {/* Tab 4: Appearance */}
        {tab === 4 && (
          <Box sx={{ p: 2 }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, color: '#111827' }}>
                    {t('appearance') || 'Appearance'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                    {t('appearance_help') ||
                      'Override colors and header/footer styling for the selected template.'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<RestartAltRoundedIcon />}
                    onClick={() => setBaseDraft((prev) => ({ ...prev, appearance: {} }))}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                  >
                    {t('reset') || 'Reset'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={() => saveWebsiteContentPatch({ appearance: cleanAppearance(baseDraft.appearance) })}
                    disabled={saving || !canUpdateSettings}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                  >
                    {t('save') || 'Save'}
                  </Button>
                </Stack>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('accent_color') || 'Accent color'}
                    value={baseDraft.appearance?.accent || ''}
                    placeholder={effectiveTheme.accent}
                    onChange={(e) => setAppearanceValue('accent', e.target.value)}
                    helperText={`${t('default') || 'Default'}: ${effectiveTheme.accent}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('page_background') || 'Page background'}
                    value={baseDraft.appearance?.pageBg || ''}
                    placeholder={effectiveTheme.pageBg}
                    onChange={(e) => setAppearanceValue('pageBg', e.target.value)}
                    helperText={`${t('default') || 'Default'}: ${effectiveTheme.pageBg}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('section_alt_background') || 'Section alt background'}
                    value={baseDraft.appearance?.sectionAltBg || ''}
                    placeholder={effectiveTheme.sectionAltBg}
                    onChange={(e) => setAppearanceValue('sectionAltBg', e.target.value)}
                    helperText={`${t('default') || 'Default'}: ${effectiveTheme.sectionAltBg}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('border_color') || 'Border color'}
                    value={baseDraft.appearance?.border || ''}
                    placeholder={effectiveTheme.border}
                    onChange={(e) => setAppearanceValue('border', e.target.value)}
                    helperText={`${t('default') || 'Default'}: ${effectiveTheme.border}`}
                  />
                </Grid>
              </Grid>

              <Typography sx={{ fontWeight: 900, color: '#111827', mt: 3, mb: 1 }}>
                {t('header') || 'Header'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    {t('header_mode') || 'Header mode'}
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    exclusive
                    value={baseDraft.appearance?.headerColor || ''}
                    onChange={(_, value) => setAppearanceValue('headerColor', value || '')}
                  >
                    <ToggleButton value="">{t('default') || 'Default'}</ToggleButton>
                    <ToggleButton value="dark">{t('dark') || 'Dark'}</ToggleButton>
                    <ToggleButton value="white">{t('light') || 'Light'}</ToggleButton>
                    <ToggleButton value="transparent">{t('transparent') || 'Transparent'}</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    {t('nav_mode') || 'Nav mode'}
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    exclusive
                    value={baseDraft.appearance?.navMode || ''}
                    onChange={(_, value) => setAppearanceValue('navMode', value || '')}
                  >
                    <ToggleButton value="">{t('default') || 'Default'}</ToggleButton>
                    <ToggleButton value="dark">{t('dark') || 'Dark'}</ToggleButton>
                    <ToggleButton value="light">{t('light') || 'Light'}</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    {t('header_scroll_mode') || 'Header on scroll mode'}
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    exclusive
                    value={baseDraft.appearance?.headerScrollColor || ''}
                    onChange={(_, value) => setAppearanceValue('headerScrollColor', value || '')}
                  >
                    <ToggleButton value="">{t('default') || 'Default'}</ToggleButton>
                    <ToggleButton value="dark">{t('dark') || 'Dark'}</ToggleButton>
                    <ToggleButton value="white">{t('light') || 'Light'}</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>

              <Typography sx={{ fontWeight: 900, color: '#111827', mt: 3, mb: 1 }}>
                {t('footer') || 'Footer'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    {t('footer_mode') || 'Footer mode'}
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    exclusive
                    value={baseDraft.appearance?.footer?.mode || ''}
                    onChange={(_, value) => setFooterAppearanceValue('mode', value || '')}
                  >
                    <ToggleButton value="">{t('default') || 'Default'}</ToggleButton>
                    <ToggleButton value="dark">{t('dark') || 'Dark'}</ToggleButton>
                    <ToggleButton value="light">{t('light') || 'Light'}</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('footer_background') || 'Footer background'}
                    value={baseDraft.appearance?.footer?.bg || ''}
                    placeholder={effectiveTheme.footer?.bg || ''}
                    onChange={(e) => setFooterAppearanceValue('bg', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('footer_text') || 'Footer text'}
                    value={baseDraft.appearance?.footer?.text || ''}
                    placeholder={effectiveTheme.footer?.text || ''}
                    onChange={(e) => setFooterAppearanceValue('text', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('footer_link') || 'Footer link'}
                    value={baseDraft.appearance?.footer?.link || ''}
                    placeholder={effectiveTheme.footer?.link || ''}
                    onChange={(e) => setFooterAppearanceValue('link', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Tab 5: Shop */}
        {tab === 5 && (
          <Box sx={{ p: 2 }}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, color: '#111827' }}>
                    {t('shop') || 'Shop'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                    {t('shop_admin_help') || 'Manage products for the public shop pages.'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<PublicRoundedIcon />}
                    onClick={() =>
                      window.open(
                        editor.type === 'partner' ? `/${editor.slug}/shop` : '/shop',
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                  >
                    {t('preview') || 'Preview'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveRoundedIcon />}
                    onClick={() => saveWebsiteContentPatch({ shop: baseDraft.shop || {} })}
                    disabled={saving || !canUpdateSettings}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                  >
                    {t('save') || 'Save'}
                  </Button>
                </Stack>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={baseDraft.shop?.enabled !== false}
                        onChange={(_, checked) => setShopValue('enabled', checked)}
                      />
                    }
                    label={t('enabled') || 'Enabled'}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('currency') || 'Currency'}
                    value={baseDraft.shop?.currency || ''}
                    onChange={(e) => setShopValue('currency', e.target.value)}
                    helperText={t('currency_help') || 'ISO currency code (e.g. NOK, USD, EUR)'}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Tabs
                  value={shopAdminTab}
                  onChange={(_, value) => setShopAdminTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={t('products') || 'Products'} />
                  <Tab label={t('banners') || 'Banners'} />
                  <Tab label={t('campaigns') || 'Campaigns'} />
                  <Tab label={t('subscriptions') || 'Subscriptions'} />
                  <Tab label={t('auctions') || 'Auctions'} />
                  <Tab label={t('categories') || 'Categories'} />
                </Tabs>
              </Box>

              <Box sx={{ mt: 2 }}>
                {shopAdminTab === 0 && (
                  <>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.5}
                      alignItems={{ md: 'center' }}
                      justifyContent="space-between"
                      sx={{ mb: 2 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                          label={(t('all') || 'All') + ` (${Array.isArray(baseDraft.shop?.products) ? baseDraft.shop.products.length : 0})`}
                          variant={shopProductFilter === 'all' ? 'filled' : 'outlined'}
                          onClick={() => setShopProductFilter('all')}
                          sx={{ fontWeight: 900 }}
                        />
                        <Chip
                          label={
                            (t('products') || 'Products') +
                            ` (${(Array.isArray(baseDraft.shop?.products) ? baseDraft.shop.products : []).filter((p) => (p?.type || 'product') !== 'service').length})`
                          }
                          variant={shopProductFilter === 'product' ? 'filled' : 'outlined'}
                          onClick={() => setShopProductFilter('product')}
                          sx={{ fontWeight: 900 }}
                        />
                        <Chip
                          label={
                            (t('services') || 'Services') +
                            ` (${(Array.isArray(baseDraft.shop?.products) ? baseDraft.shop.products : []).filter((p) => (p?.type || '') === 'service').length})`
                          }
                          variant={shopProductFilter === 'service' ? 'filled' : 'outlined'}
                          onClick={() => setShopProductFilter('service')}
                          sx={{ fontWeight: 900 }}
                        />
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          startIcon={<AddRoundedIcon />}
                          onClick={() => openProductEditor({ type: 'product', published: true }, -1)}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                        >
                          {t('new_product') || 'New product'}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<AddRoundedIcon />}
                          onClick={() => openProductEditor({ type: 'service', published: true }, -1)}
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                        >
                          {t('new_service') || 'New service'}
                        </Button>
                      </Stack>
                    </Stack>

                    <MaterialTable
                      icons={TableIcons}
                      title=""
                      columns={[
                        { title: t('id') || 'ID', field: 'id' },
                        {
                          title: t('type') || 'Type',
                          field: 'type',
                          render: (rowData) => (rowData?.type === 'service' ? (t('service') || 'Service') : (t('product') || 'Product')),
                        },
                        { title: t('title') || 'Title', field: 'title' },
                        { title: t('price') || 'Price', field: 'price', type: 'numeric' },
                        {
                          title: t('quantity') || 'Qty',
                          field: 'stock',
                          type: 'numeric',
                          render: (rowData) => (rowData?.type === 'service' ? '—' : (rowData?.stock ?? 0)),
                        },
                        {
                          title: t('category') || 'Category',
                          field: 'category',
                          render: (rowData) => {
                            const key = String(rowData?.category || '').trim();
                            if (!key) return '';
                            const cats = Array.isArray(baseDraft.shop?.categories) ? baseDraft.shop.categories : [];
                            const match = cats.find((cat) => String(cat?.id || '') === key);
                            return match?.title || key;
                          },
                        },
                        { title: t('published') || 'Published', field: 'published', type: 'boolean' },
                        {
                          title: t('image') || 'Image',
                          field: 'image',
                          render: (rowData) => {
                            const url =
                              rowData?.image ||
                              (Array.isArray(rowData?.images) && rowData.images.length ? rowData.images[0] : '');
                            if (!url) return '';
                            return (
                              <Box
                                component="img"
                                alt=""
                                src={url}
                                sx={{
                                  width: 62,
                                  height: 44,
                                  objectFit: 'cover',
                                  borderRadius: 1.5,
                                  border: '1px solid #e5e7eb',
                                  bgcolor: '#f8fafc',
                                }}
                              />
                            );
                          },
                        },
                      ]}
                      data={(Array.isArray(baseDraft.shop?.products) ? baseDraft.shop.products : [])
                        .map((product, index) => ({ ...product, _index: index }))
                        .filter((product) => {
                          const type = product?.type === 'service' ? 'service' : 'product';
                          if (shopProductFilter === 'all') return true;
                          return type === shopProductFilter;
                        })}
                      options={{
                        actionsColumnIndex: -1,
                        paging: false,
                        search: true,
                        headerStyle: { fontWeight: 800 },
                        rowStyle: { fontSize: 14 },
                      }}
                      actions={[
                        {
                          icon: () => <OpenInNewRoundedIcon />,
                          tooltip: t('preview') || 'Preview',
                          onClick: (_, rowData) => {
                            const base = editor.type === 'partner' ? `/${editor.slug}/shop` : '/shop';
                            const id = rowData?.id ? encodeURIComponent(String(rowData.id)) : '';
                            if (!id) return;
                            window.open(`${base}/${id}`, '_blank', 'noopener,noreferrer');
                          },
                        },
                        {
                          icon: () => <EditRoundedIcon />,
                          tooltip: t('edit') || 'Edit',
                          onClick: (_, rowData) => {
                            const idx = rowData?._index;
                            openProductEditor(rowData, typeof idx === 'number' ? idx : -1);
                          },
                        },
                        {
                          icon: () => <DeleteRoundedIcon />,
                          tooltip: t('delete') || 'Delete',
                          onClick: (_, rowData) => {
                            const idx = rowData?._index;
                            if (typeof idx !== 'number' || idx < 0) return;
                            if (!window.confirm(t('confirm_delete') || 'Delete this item?')) return;
                            const current = Array.isArray(baseDraft.shop?.products) ? [...baseDraft.shop.products] : [];
                            current.splice(idx, 1);
                            setShopValue('products', current);
                          },
                        },
                      ]}
                    />

                    <ShopProductEditorDialog
                      open={productEditor.open}
                      onClose={closeProductEditor}
                      value={productEditor.product}
                      onSave={handleSaveShopProduct}
                      currency={baseDraft.shop?.currency || 'USD'}
                      categories={Array.isArray(baseDraft.shop?.categories) ? baseDraft.shop.categories : []}
                      languages={languages}
                      uploadPathPrefix={`website_assets/storefront/shop/${editor.type === 'partner' ? editor.slug : 'global'}`}
                      previewBaseUrl={editor.type === 'partner' ? `/${editor.slug}/shop` : '/shop'}
                    />
                  </>
                )}

                {shopAdminTab === 1 && (
                    <MaterialTable
                      icons={TableIcons}
                      title={t('banners') || 'Banners'}
                      columns={[
                        { title: t('id') || 'ID', field: 'id' },
                        { title: t('title') || 'Title', field: 'title' },
                        { title: t('subtitle') || 'Subtitle', field: 'subtitle' },
                        { title: t('variant') || 'Variant', field: 'variant' },
                        { title: t('active') || 'Active', field: 'active', type: 'boolean' },
                        { title: t('starts_at') || 'Starts at', field: 'startsAt' },
                        { title: t('ends_at') || 'Ends at', field: 'endsAt' },
                        {
                          title: t('image_url') || 'Image URL',
                          field: 'image',
                          render: (rowData) =>
                            rowData.image ? (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Box
                                  component="img"
                                  alt=""
                                  src={rowData.image}
                                  sx={{
                                    width: 54,
                                    height: 36,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    border: '1px solid #e5e7eb',
                                    bgcolor: '#f8fafc',
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    maxWidth: 180,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    color: '#111827',
                                  }}
                                >
                                  {rowData.image}
                                </Typography>
                              </Stack>
                            ) : (
                              ''
                            ),
                        },
                        { title: t('link') || 'Link', field: 'link' },
                        { title: t('button_text') || 'Button text', field: 'buttonText' },
                      ]}
                    data={Array.isArray(baseDraft.shop?.banners) ? baseDraft.shop.banners : []}
                    options={{
                      actionsColumnIndex: -1,
                      paging: false,
                      search: true,
                      headerStyle: { fontWeight: 800 },
                      rowStyle: { fontSize: 14 },
                    }}
                    editable={{
                      onRowAdd: (newData) =>
                        new Promise((resolve) => {
                          const current = Array.isArray(baseDraft.shop?.banners) ? baseDraft.shop.banners : [];
                          const nextId = newData.id || slugify(newData.title) || `banner-${Date.now()}`;
                          const normalized = {
                            ...newData,
                            id: nextId,
                            active: newData.active !== false,
                          };
                          setShopValue('banners', [...current, normalized]);
                          resolve();
                        }),
                      onRowUpdate: (newData, oldData) =>
                        new Promise((resolve) => {
                          if (!oldData) return resolve();
                          const current = Array.isArray(baseDraft.shop?.banners) ? [...baseDraft.shop.banners] : [];
                          const idx = oldData.tableData?.id;
                          if (typeof idx !== 'number' || idx < 0 || idx >= current.length) return resolve();
                          const nextId = newData.id || oldData.id || slugify(newData.title) || `banner-${Date.now()}`;
                          current[idx] = {
                            ...oldData,
                            ...newData,
                            id: nextId,
                            active: newData.active !== false,
                          };
                          setShopValue('banners', current);
                          resolve();
                        }),
                      onRowDelete: (oldData) =>
                        new Promise((resolve) => {
                          const current = Array.isArray(baseDraft.shop?.banners) ? [...baseDraft.shop.banners] : [];
                          const idx = oldData.tableData?.id;
                          if (typeof idx !== 'number' || idx < 0 || idx >= current.length) return resolve();
                          current.splice(idx, 1);
                          setShopValue('banners', current);
                          resolve();
                        }),
                    }}
                  />
                )}

                {shopAdminTab === 2 && (
                  <MaterialTable
                    icons={TableIcons}
                    title={t('campaigns') || 'Campaigns'}
                    columns={[
                      { title: t('id') || 'ID', field: 'id' },
                      { title: t('title') || 'Title', field: 'title' },
                      { title: t('description') || 'Description', field: 'description' },
                      { title: t('active') || 'Active', field: 'active', type: 'boolean' },
                      { title: t('starts_at') || 'Starts at', field: 'startsAt' },
                      { title: t('ends_at') || 'Ends at', field: 'endsAt' },
                      { title: t('discount_percent') || 'Discount %', field: 'discountPercent', type: 'numeric' },
                      {
                        title: t('product_ids') || 'Product IDs',
                        field: 'productIds',
                        render: (rowData) => stringifyDelimitedList(rowData.productIds),
                      },
                      { title: t('badge') || 'Badge', field: 'badgeText' },
                      {
                        title: t('banner_image') || 'Banner image',
                        field: 'bannerImage',
                        render: (rowData) =>
                          rowData.bannerImage ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box
                                component="img"
                                alt=""
                                src={rowData.bannerImage}
                                sx={{
                                  width: 54,
                                  height: 36,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: '1px solid #e5e7eb',
                                  bgcolor: '#f8fafc',
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 180,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: '#111827',
                                }}
                              >
                                {rowData.bannerImage}
                              </Typography>
                            </Stack>
                          ) : (
                            ''
                          ),
                      },
                    ]}
                    data={Array.isArray(baseDraft.shop?.campaigns) ? baseDraft.shop.campaigns : []}
                    options={{
                      actionsColumnIndex: -1,
                      paging: false,
                      search: true,
                      headerStyle: { fontWeight: 800 },
                      rowStyle: { fontSize: 14 },
                    }}
                    editable={{
                      onRowAdd: (newData) =>
                        new Promise((resolve) => {
                          const current = Array.isArray(baseDraft.shop?.campaigns) ? baseDraft.shop.campaigns : [];
                          const nextId = newData.id || slugify(newData.title) || `campaign-${Date.now()}`;
                          const normalized = {
                            ...newData,
                            id: nextId,
                            active: newData.active !== false,
                            discountPercent: Number(newData.discountPercent || 0),
                            productIds: parseDelimitedList(newData.productIds),
                          };
                          setShopValue('campaigns', [...current, normalized]);
                          resolve();
                        }),
                      onRowUpdate: (newData, oldData) =>
                        new Promise((resolve) => {
                          if (!oldData) return resolve();
                          const current = Array.isArray(baseDraft.shop?.campaigns) ? [...baseDraft.shop.campaigns] : [];
                          const idx = oldData.tableData?.id;
                          if (typeof idx !== 'number' || idx < 0 || idx >= current.length) return resolve();
                          const nextId = newData.id || oldData.id || slugify(newData.title) || `campaign-${Date.now()}`;
                          current[idx] = {
                            ...oldData,
                            ...newData,
                            id: nextId,
                            active: newData.active !== false,
                            discountPercent: Number(newData.discountPercent || 0),
                            productIds: parseDelimitedList(newData.productIds),
                          };
                          setShopValue('campaigns', current);
                          resolve();
                        }),
                      onRowDelete: (oldData) =>
                        new Promise((resolve) => {
                          const current = Array.isArray(baseDraft.shop?.campaigns) ? [...baseDraft.shop.campaigns] : [];
                          const idx = oldData.tableData?.id;
                          if (typeof idx !== 'number' || idx < 0 || idx >= current.length) return resolve();
                          current.splice(idx, 1);
                          setShopValue('campaigns', current);
                          resolve();
                        }),
                    }}
                  />
                )}

                {shopAdminTab === 3 && (
                  <MaterialTable
                    icons={TableIcons}
                    title={t('subscriptions') || 'Subscriptions'}
                    columns={[
                      { title: t('id') || 'ID', field: 'id' },
                      { title: t('title') || 'Title', field: 'title' },
                      { title: t('description') || 'Description', field: 'description' },
                      { title: t('price') || 'Price', field: 'price', type: 'numeric' },
                      { title: t('interval') || 'Interval', field: 'interval' },
                      {
                        title: t('perks') || 'Perks',
                        field: 'perks',
                        render: (rowData) => stringifyDelimitedList(rowData.perks),
                      },
                      { title: t('published') || 'Published', field: 'published', type: 'boolean' },
                      { title: t('image_url') || 'Image URL', field: 'image' },
                    ]}
                    data={Array.isArray(baseDraft.shop?.subscriptions) ? baseDraft.shop.subscriptions : []}
                    options={{
                      actionsColumnIndex: -1,
                      paging: false,
                      search: true,
                      headerStyle: { fontWeight: 800 },
                      rowStyle: { fontSize: 14 },
                    }}
                    editable={{
                      onRowAdd: (newData) =>
                        new Promise((resolve) => {
                          const current = Array.isArray(baseDraft.shop?.subscriptions) ? baseDraft.shop.subscriptions : [];
                          const nextId = newData.id || slugify(newData.title) || `plan-${Date.now()}`;
                          const normalized = {
                            ...newData,
                            id: nextId,
                            price: Number(newData.price || 0),
                            perks: parseDelimitedList(newData.perks),
                            published: newData.published !== false,
                          };
                          setShopValue('subscriptions', [...current, normalized]);
                          resolve();
                        }),
                      onRowUpdate: (newData, oldData) =>
                        new Promise((resolve) => {
                          if (!oldData) return resolve();
                          const current = Array.isArray(baseDraft.shop?.subscriptions) ? [...baseDraft.shop.subscriptions] : [];
                          const idx = oldData.tableData?.id;
                          if (typeof idx !== 'number' || idx < 0 || idx >= current.length) return resolve();
                          const nextId = newData.id || oldData.id || slugify(newData.title) || `plan-${Date.now()}`;
                          current[idx] = {
                            ...oldData,
                            ...newData,
                            id: nextId,
                            price: Number(newData.price || 0),
                            perks: parseDelimitedList(newData.perks),
                            published: newData.published !== false,
                          };
                          setShopValue('subscriptions', current);
                          resolve();
                        }),
                      onRowDelete: (oldData) =>
                        new Promise((resolve) => {
                          const current = Array.isArray(baseDraft.shop?.subscriptions) ? [...baseDraft.shop.subscriptions] : [];
                          const idx = oldData.tableData?.id;
                          if (typeof idx !== 'number' || idx < 0 || idx >= current.length) return resolve();
                          current.splice(idx, 1);
                          setShopValue('subscriptions', current);
                          resolve();
                        }),
                    }}
                  />
                )}

                {shopAdminTab === 4 && (
                  <MaterialTable
                    icons={TableIcons}
                    title={t('auctions') || 'Auctions'}
                    columns={[
                      { title: t('id') || 'ID', field: 'id' },
                      { title: t('title') || 'Title', field: 'title' },
                      { title: t('description') || 'Description', field: 'description' },
                      { title: t('published') || 'Published', field: 'published', type: 'boolean' },
                      { title: t('starts_at') || 'Starts at', field: 'startsAt' },
                      { title: t('ends_at') || 'Ends at', field: 'endsAt' },
                      { title: t('starting_bid') || 'Starting bid', field: 'startingBid', type: 'numeric' },
                      { title: t('bid_increment') || 'Bid increment', field: 'bidIncrement', type: 'numeric' },
                      {
                        title: t('image_url') || 'Image URL',
                        field: 'image',
                        render: (rowData) =>
                          rowData.image ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box
                                component="img"
                                alt=""
                                src={rowData.image}
                                sx={{
                                  width: 54,
                                  height: 36,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: '1px solid #e5e7eb',
                                  bgcolor: '#f8fafc',
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 180,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color: '#111827',
                                }}
                              >
                                {rowData.image}
                              </Typography>
                            </Stack>
                          ) : (
                            ''
                          ),
                      },
                    ]}
                    data={Array.isArray(baseDraft.shop?.auctions) ? baseDraft.shop.auctions : []}
                    options={{
                      actionsColumnIndex: -1,
                      paging: false,
                      search: true,
                      headerStyle: { fontWeight: 800 },
                      rowStyle: { fontSize: 14 },
                    }}
                    editable={{
                      onRowAdd: (newData) =>
                        new Promise((resolve) => {
                          const current = Array.isArray(baseDraft.shop?.auctions) ? baseDraft.shop.auctions : [];
                          const nextId = newData.id || slugify(newData.title) || `auction-${Date.now()}`;
                          const normalized = {
                            ...newData,
                            id: nextId,
                            startingBid: Number(newData.startingBid || 0),
                            bidIncrement: Number(newData.bidIncrement || 1),
                            published: newData.published !== false,
                          };
                          setShopValue('auctions', [...current, normalized]);
                          resolve();
                        }),
                      onRowUpdate: (newData, oldData) =>
                        new Promise((resolve) => {
                          if (!oldData) return resolve();
                          const current = Array.isArray(baseDraft.shop?.auctions) ? [...baseDraft.shop.auctions] : [];
                          const idx = oldData.tableData?.id;
                          if (typeof idx !== 'number' || idx < 0 || idx >= current.length) return resolve();
                          const nextId = newData.id || oldData.id || slugify(newData.title) || `auction-${Date.now()}`;
                          current[idx] = {
                            ...oldData,
                            ...newData,
                            id: nextId,
                            startingBid: Number(newData.startingBid || 0),
                            bidIncrement: Number(newData.bidIncrement || 1),
                            published: newData.published !== false,
                          };
                          setShopValue('auctions', current);
                          resolve();
                        }),
                      onRowDelete: (oldData) =>
                        new Promise((resolve) => {
                          const current = Array.isArray(baseDraft.shop?.auctions) ? [...baseDraft.shop.auctions] : [];
                          const idx = oldData.tableData?.id;
                          if (typeof idx !== 'number' || idx < 0 || idx >= current.length) return resolve();
                          current.splice(idx, 1);
                          setShopValue('auctions', current);
                          resolve();
                        }),
                    }}
                  />
                )}

                {shopAdminTab === 5 && (
                  <>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.5}
                      alignItems={{ md: 'center' }}
                      justifyContent="space-between"
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: '#111827' }}>
                          {t('categories') || 'Categories'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                          {t('categories_help') || 'Define product categories for better filters and grouping.'}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={<AddRoundedIcon />}
                        onClick={() => openCategoryEditor({ published: true }, -1)}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                      >
                        {t('new_category') || 'New category'}
                      </Button>
                    </Stack>

                    <MaterialTable
                      icons={TableIcons}
                      title=""
                      columns={[
                        { title: t('id') || 'ID', field: 'id' },
                        { title: t('title') || 'Title', field: 'title' },
                        { title: t('parent_category') || 'Parent', field: 'parentId' },
                        { title: t('published') || 'Published', field: 'published', type: 'boolean' },
                        { title: t('featured') || 'Featured', field: 'featured', type: 'boolean' },
                        {
                          title: t('image') || 'Image',
                          field: 'image',
                          render: (rowData) => {
                            const url = rowData?.image;
                            if (!url) return '';
                            return (
                              <Box
                                component="img"
                                alt=""
                                src={url}
                                sx={{
                                  width: 62,
                                  height: 44,
                                  objectFit: 'cover',
                                  borderRadius: 1.5,
                                  border: '1px solid #e5e7eb',
                                  bgcolor: '#f8fafc',
                                }}
                              />
                            );
                          },
                        },
                      ]}
                      data={(Array.isArray(baseDraft.shop?.categories) ? baseDraft.shop.categories : []).map((cat, index) => ({
                        ...cat,
                        _index: index,
                      }))}
                      options={{
                        actionsColumnIndex: -1,
                        paging: false,
                        search: true,
                        headerStyle: { fontWeight: 800 },
                        rowStyle: { fontSize: 14 },
                      }}
                      actions={[
                        {
                          icon: () => <EditRoundedIcon />,
                          tooltip: t('edit') || 'Edit',
                          onClick: (event, rowData) => {
                            const idx = rowData?._index;
                            if (typeof idx !== 'number') return;
                            const current = Array.isArray(baseDraft.shop?.categories) ? baseDraft.shop.categories : [];
                            openCategoryEditor(current[idx], idx);
                          },
                        },
                        {
                          icon: () => <DeleteRoundedIcon />,
                          tooltip: t('delete') || 'Delete',
                          onClick: (event, rowData) => {
                            const idx = rowData?._index;
                            if (typeof idx !== 'number') return;
                            const current = Array.isArray(baseDraft.shop?.categories) ? [...baseDraft.shop.categories] : [];
                            const id = current[idx]?.id;
                            const usedCount = (Array.isArray(baseDraft.shop?.products) ? baseDraft.shop.products : []).filter(
                              (p) => String(p?.category || '') === String(id || '')
                            ).length;
                            const label = current[idx]?.title || id || '';
                            const msg = usedCount
                              ? `Delete category "${label}"? It is used by ${usedCount} product(s).`
                              : `Delete category "${label}"?`;
                            if (!window.confirm(msg)) return;
                            current.splice(idx, 1);
                            setShopValue('categories', current);
                          },
                        },
                      ]}
                    />

                    <ShopCategoryEditorDialog
                      open={categoryEditor.open}
                      onClose={closeCategoryEditor}
                      value={categoryEditor.category}
                      onSave={handleSaveShopCategory}
                      categories={Array.isArray(baseDraft.shop?.categories) ? baseDraft.shop.categories : []}
                      languages={languages}
                      uploadPathPrefix={`website_assets/storefront/shop/categories/${editor.type === 'partner' ? editor.slug : 'global'}`}
                    />
                  </>
                )}
              </Box>
            </Paper>
          </Box>
        )}
      </Paper>

      {/* Overlay preview (matches the PHP "View" overlay behavior) */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            bgcolor: '#0b0b0b',
            color: '#fff',
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontWeight: 800 }}>{previewTitle}</Typography>
            <IconButton onClick={() => setPreviewOpen(false)} sx={{ color: '#fff' }}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#0b0b0b' }}>
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            <iframe
              title={previewTitle}
              src={
                editor.type === 'partner'
                  ? `/${encodeURIComponent(editor.slug)}?storefront=1&previewTemplate=${encodeURIComponent(previewTemplate)}&embedded=1`
                  : `/?previewTemplate=${encodeURIComponent(previewTemplate)}&embedded=1`
              }
              style={{ width: '100%', height: '75vh', border: 0, display: 'block' }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
